import { useEffect, useRef, useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import { useProposalStream } from '@/common/useProposalStream'
import { formatRefineUserMessage } from '@/components/proposal/refineMessage'
import type { ProposalSectionMessage } from '@/api/proposals/proposals'

// Section-scoped refine/regenerate for the canvas panel: one shared stream instance (the same one
// the legacy proposal route wraps), with the endpoint/body/optimistic conventions baked in so
// callers only supply a section id and an instruction. The router comes from TanStack context
// because useProposalStream invalidates the route after every close or stop.
export function useSectionRefine({
  projectId,
  proposalId,
  onSettled
}: {
  projectId: number
  proposalId: number
  onSettled: (sectionId: number | null) => void
}) {
  const router = useRouter()
  // Hoisted so the close handler can clear the closed attempt's local turns without referencing
  // `stream` before it exists (same trick as the legacy route's clearBuffersRef).
  const clearBuffersRef = useRef<(sectionId: number) => void>(() => {})
  const stream = useProposalStream({
    router,
    onClose: (closedSectionId) => {
      // The settle-reload refetches persisted history; this attempt's optimistic and committed
      // turns must go with it or they would linger beside the fresh rows.
      if (closedSectionId !== null) clearBuffersRef.current(closedSectionId)
      onSettled(closedSectionId)
    }
  })
  clearBuffersRef.current = stream.clearSectionBuffers

  // stream.error is panel-global because the whole panel shares one stream, so the adapter owns
  // error scoping: each attempt records its section, an arriving error is attributed to that
  // section, and errorFor hands the message to its owner's composer alone. Every new attempt
  // clears any previous error first, so a stale message can never resurface elsewhere.
  const pendingSectionRef = useRef<number | null>(null)
  const [failedSectionId, setFailedSectionId] = useState<number | null>(null)

  useEffect(() => {
    if (stream.error === null) {
      setFailedSectionId(null)
      return
    }
    const owner = pendingSectionRef.current
    pendingSectionRef.current = null
    if (owner !== null) setFailedSectionId(owner)
  }, [stream.error])

  function beginAttempt(sectionId: number) {
    pendingSectionRef.current = sectionId
    if (stream.error !== null) stream.clearError()
  }

  function startRefine(sectionId: number, instruction: string, excerpt?: string) {
    beginAttempt(sectionId)
    // The excerpt joins the body only when provided (exactOptionalPropertyTypes).
    const body: Record<string, unknown> = { instruction, modelTemp: 0.5 }
    if (excerpt !== undefined) body['excerpt'] = excerpt
    stream.start(`/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}/refine`, body, {
      sectionId,
      userText: formatRefineUserMessage(instruction, excerpt)
    })
  }

  function startRegenerate(sectionId: number) {
    beginAttempt(sectionId)
    // No optimistic user turn exists for a regenerate, so the section id is passed explicitly as
    // targetSectionId; that keeps warning clears and close attribution scoped to this section.
    stream.start(
      `/api/projects/${projectId}/proposals/${proposalId}/sections/${sectionId}/regenerate`,
      { modelTemp: 0.5 },
      undefined,
      sectionId
    )
  }

  // Per-section reads mirror the legacy proposal route: live content is keyed by section in
  // streamingBySectionId, gated by the global streaming flag and which section owns the stream
  // (activeSectionId). A sibling section must read as idle while another streams.
  function isStreaming(sectionId: number): boolean {
    return stream.isStreaming && stream.activeSectionId === sectionId
  }

  function streamingText(sectionId: number): string | undefined {
    if (!stream.isStreaming || stream.activeSectionId !== sectionId) return undefined
    return stream.streamingBySectionId[sectionId]
  }

  function optimisticTurn(sectionId: number): ProposalSectionMessage | undefined {
    return stream.optimisticBySectionId[sectionId]
  }

  function committedTurn(sectionId: number): ProposalSectionMessage | undefined {
    return stream.committedBySectionId[sectionId]
  }

  function errorFor(sectionId: number): string | null {
    return failedSectionId === sectionId ? stream.error : null
  }

  return {
    startRefine,
    startRegenerate,
    stop: stream.stop,
    isStreaming,
    streamingText,
    optimisticTurn,
    committedTurn,
    errorFor
  }
}
